import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  BrainCircuit,
  FileStack,
  RefreshCw,
  ShieldCheck,
  ShieldEllipsis,
  Users,
} from "lucide-react";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import config from "../config/config";
import CockpitShell from "../components/CockpitShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { clearStoredAuth } from "../utils/authSession";

const navItems = [
  { href: "/dashboard/admin", label: "Command", icon: BrainCircuit },
  { href: "/dashboard/admin/assessment-ops", label: "Assessment", icon: FileStack },
  { href: "/dashboard/admin/users", label: "Users", icon: Users },
  { href: "/dashboard/security", label: "Security", icon: ShieldCheck },
  { href: "/dashboard/gpt", label: "GPT", icon: Bot },
];

const emptyOverview = {
  generatedAt: "",
  summary: {
    suspicious24h: 0,
    blocked24h: 0,
    uniqueAttackIps24h: 0,
    activeBans: 0,
    totalBans: 0,
    securityEvents: 0,
  },
  current: {
    suspicious15m: 0,
    blocked15m: 0,
    uniqueAttackIps15m: 0,
    statusLabel: "Ruhig",
    statusTone: "low",
    hottestTarget: null,
    lastSeenAt: null,
    lastBlockedAt: null,
  },
  history: {
    bans7d: 0,
    unbans7d: 0,
    securityEvents7d: 0,
    lastBanAt: null,
  },
  timeline: { labels: [], suspicious: [], blocked: [] },
  topIps: [],
  topTargets: [],
  topTags: [],
  feed: [],
  fail2ban: { active: [], recent: [] },
  securityEvents: { recent: [] },
  sources: { debugLogPath: "", fail2banLogPath: "", debugFeedCount: 0 },
};

function formatTimestamp(value) {
  if (!value) return "Noch nichts erfasst";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unklar";
  return date.toLocaleString("de-DE", { hour12: false });
}

function currentStatusText(current) {
  if (current.suspicious15m > 12) return "Im Moment ist deutlicher Angriffsverkehr sichtbar.";
  if (current.suspicious15m > 0) return "Im Moment sehen wir aktive Probe- oder Angriffsversuche.";
  return "Im Moment ist die API ruhig.";
}

function currentStatusClass(tone) {
  if (tone === "high") return "security-ops-status security-ops-status-high";
  if (tone === "medium") return "security-ops-status security-ops-status-medium";
  return "security-ops-status security-ops-status-low";
}

function metricTone(value, kind = "default") {
  const numeric = Number(value || 0);
  if (kind === "blocked") {
    if (numeric > 0) return "security-ops-metric-warn";
    return "security-ops-metric-ok";
  }
  if (numeric > 0) return "security-ops-metric-active";
  return "security-ops-metric-idle";
}

function buildCurrentFeed(feed) {
  return (feed || []).slice(0, 12).map((entry, index) => ({
    id: `${entry.requestId || index}-${entry.timestamp || index}`,
    time: formatTimestamp(entry.timestamp),
    ip: entry.ip || "-",
    path: entry.url || "-",
    type: entry.label || entry.tag || "-",
    status: entry.status ?? "-",
    tone: Number(entry.status || 0) >= 400 ? "warn" : "neutral",
  }));
}

function buildHistoryRows(overview) {
  const fail2banRows = (overview.fail2ban.recent || []).map((entry, index) => ({
    id: `f2b-${entry.timestamp || index}-${entry.ip || index}`,
    time: formatTimestamp(entry.timestamp),
    source: "Fail2Ban",
    type: entry.action || "Ban",
    ip: entry.ip || "-",
    target: entry.jail || "-",
    status: entry.country || "?",
    tone: entry.action === "Ban" ? "warn" : "neutral",
  }));

  const eventRows = (overview.securityEvents.recent || []).map((entry, index) => ({
    id: `sec-${entry.timestamp || index}-${entry.ip || index}`,
    time: formatTimestamp(entry.timestamp),
    source: "Security Event",
    type: entry.tag || "Event",
    ip: entry.ip || "-",
    target: entry.url || "-",
    status: entry.status ?? "-",
    tone: Number(entry.status || 0) >= 400 ? "warn" : "neutral",
  }));

  return [...fail2banRows, ...eventRows]
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, 18);
}

function buildChartData(overview) {
  return {
    labels: overview.timeline.labels || [],
    datasets: [
      {
        label: "Auffaellig",
        data: overview.timeline.suspicious || [],
        borderColor: "#2563ff",
        backgroundColor: "rgba(37, 99, 255, 0.12)",
        pointRadius: 3,
        borderWidth: 2,
        tension: 0.28,
        fill: true,
      },
      {
        label: "Geblockt",
        data: overview.timeline.blocked || [],
        borderColor: "#d74a6a",
        backgroundColor: "rgba(215, 74, 106, 0.08)",
        pointRadius: 3,
        borderWidth: 2,
        tension: 0.22,
        fill: true,
      },
    ],
  };
}

export default function SecurityDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [overview, setOverview] = useState(emptyOverview);
  const [state, setState] = useState({ loading: true, error: "", refreshing: false });
  const [accessState, setAccessState] = useState({
    open: false,
    title: "",
    message: "",
    actionLabel: "",
    actionHref: "/login",
  });

  const handleAccessError = (error) => {
    const status = Number(error?.response?.status || error?.status || 0);
    if (status === 401) {
      clearStoredAuth();
      setAccessState({
        open: true,
        title: "Sitzung abgelaufen",
        message: "Deine Admin-Sitzung ist nicht mehr gueltig. Bitte melde dich erneut an, damit wir aktuelle Security-Daten laden koennen.",
        actionLabel: "Zur Anmeldung",
        actionHref: "/login",
      });
      return "Die Security-API antwortet mit 401. Dein Admin-Login ist abgelaufen.";
    }

    if (status === 403) {
      setAccessState({
        open: true,
        title: "Kein Zugriff",
        message: "Dieses Security-Dashboard ist nur fuer Admin-Zugaenge freigeschaltet. Dein aktueller Account darf die Live-Daten nicht lesen.",
        actionLabel: "Zum Admin-Login",
        actionHref: "/login",
      });
      return "Die Security-API antwortet mit 403. Der aktuelle Account hat keine Admin-Berechtigung.";
    }

    return "Die Security-Daten konnten nicht geladen werden.";
  };

  const loadOverview = async (refreshing = false) => {
    setState((current) => ({ ...current, loading: !refreshing, refreshing, error: "" }));
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/security-overview`, {
        headers: authHeaders,
        params: { t: Date.now() },
      });
      setOverview({ ...emptyOverview, ...(res.data || {}) });
      setState({ loading: false, error: "", refreshing: false });
    } catch (error) {
      setState({
        loading: false,
        refreshing: false,
        error: handleAccessError(error),
      });
      setOverview(emptyOverview);
    }
  };

  useEffect(() => {
    document.title = "Experts4Europe | Security Monitor";
    loadOverview(false);
  }, []);

  const currentFeedRows = buildCurrentFeed(overview.feed);
  const historyRows = buildHistoryRows(overview);
  const chartData = buildChartData(overview);

  const summaryStrip = [
    {
      label: "Auffaellig jetzt",
      value: overview.current.suspicious15m,
      note: "letzte 15 Minuten",
      tone: metricTone(overview.current.suspicious15m),
    },
    {
      label: "Geblockt jetzt",
      value: overview.current.blocked15m,
      note: "direkt abgewehrt",
      tone: metricTone(overview.current.blocked15m, "blocked"),
    },
    {
      label: "Aktive Sperren",
      value: overview.summary.activeBans,
      note: `gesamt ${overview.summary.totalBans}`,
      tone: metricTone(overview.summary.activeBans),
    },
    {
      label: "Auffaellig 24h",
      value: overview.summary.suspicious24h,
      note: "historische Einordnung",
      tone: metricTone(overview.summary.suspicious24h),
    },
  ];

  return (
    <CockpitShell
      title="Security Monitor"
      subtitle="Live-Lage, Sperren, Verlauf und historische Vorfaelle in einer klaren Admin-Sicht."
      badge="Security Monitor"
      navItems={navItems}
      wideLayout
      layoutClassName="dashboard-layout-ops security-monitor-layout"
      pageClassName="dashboard-page-ops security-monitor-page"
      contentClassName="dashboard-content-ops security-monitor-content"
      theme="admin"
      shellLabel="Operations Security"
      shellDescription="Klare Sicht auf aktuelle Lage, letzte Vorfaelle und die Reaktion der Schutzsysteme."
      modeLabel="Defense"
      liveLabel="Admin"
      utilityHref={null}
      topSignal="Security"
      topContext="Europe API"
      actions={(
        <Button
          variant="outline"
          className="dashboard-action-button dashboard-action-button-secondary"
          onClick={() => loadOverview(true)}
          disabled={state.refreshing}
        >
          <RefreshCw size={16} strokeWidth={2} className={state.refreshing ? "animate-spin" : ""} />
          Aktualisieren
        </Button>
      )}
    >
      <section className="security-ops-workspace">
        <Dialog open={accessState.open} onOpenChange={(open) => setAccessState((current) => ({ ...current, open }))}>
          <DialogContent className="dashboard-session-dialog">
            <DialogHeader>
              <DialogTitle>{accessState.title}</DialogTitle>
              <DialogDescription>{accessState.message}</DialogDescription>
            </DialogHeader>
            <div className="dashboard-session-dialog__actions">
              <Button variant="outline" onClick={() => navigate("/")}>
                Startseite
              </Button>
              <Button onClick={() => navigate(accessState.actionHref)}>
                {accessState.actionLabel}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {state.error ? (
          <div className="security-state security-state-error">
            <AlertTriangle size={18} strokeWidth={2} />
            <span>{state.error}</span>
          </div>
        ) : null}

        <div className="security-ops-summary-strip">
          {summaryStrip.map((item) => (
            <article key={item.label} className={`security-ops-summary-strip__card ${item.tone}`}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <p>{item.note}</p>
            </article>
          ))}
        </div>

        <div className="security-ops-grid-top">
          <article className="dashboard-mission-board security-ops-primary-stage">
            <div className="security-ops-primary__head">
              <div>
                <span className="dashboard-kicker">Jetzt</span>
                <h2 className="dashboard-section-title">Was passiert gerade?</h2>
                <p className="dashboard-section-copy">{currentStatusText(overview.current)}</p>
              </div>
              <div className={currentStatusClass(overview.current.statusTone)}>
                <ShieldEllipsis size={18} strokeWidth={2} />
                <div>
                  <strong>{overview.current.statusLabel}</strong>
                  <span>Stand {formatTimestamp(overview.generatedAt)}</span>
                </div>
              </div>
            </div>

            <div className="security-ops-livebar">
              <div className="security-ops-livebar__item">
                <span>Letzter Angriff</span>
                <strong>{formatTimestamp(overview.current.lastSeenAt)}</strong>
              </div>
              <div className="security-ops-livebar__item">
                <span>Letzter Block</span>
                <strong>{formatTimestamp(overview.current.lastBlockedAt)}</strong>
              </div>
              <div className="security-ops-livebar__item">
                <span>Heissestes Ziel</span>
                <strong>{overview.current.hottestTarget?.path || "Im Moment nichts"}</strong>
              </div>
            </div>

            <div className="security-ops-focus-band">
              <div className="security-ops-focus-band__copy">
                <span className="dashboard-kicker">Live-Prioritaet</span>
                <strong>{overview.current.hottestTarget?.path || "Kein heisses Ziel"}</strong>
                <p>
                  {overview.current.hottestTarget
                    ? `${overview.current.hottestTarget.count} Treffer im aktuellen 15-Minuten-Fenster.`
                    : "Im Moment liegt kein dominantes Ziel vor. Die Lage bleibt ruhig und ohne heisse Pfade."}
                </p>
              </div>
              <div className="security-ops-focus-band__stats">
                <div>
                  <span>Quellen jetzt</span>
                  <strong>{overview.current.uniqueAttackIps15m}</strong>
                </div>
                <div>
                  <span>Geblockt jetzt</span>
                  <strong>{overview.current.blocked15m}</strong>
                </div>
                <div>
                  <span>Aktive Sperren</span>
                  <strong>{overview.summary.activeBans}</strong>
                </div>
              </div>
            </div>
          </article>

          <section className="dashboard-rail-card security-ops-rail security-ops-rail-compact">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Aktiv</span>
                <h2 className="dashboard-section-title">Aktive Sperren</h2>
              </div>
            </div>
            <div className="security-ops-banlist">
              {overview.fail2ban.active.length ? overview.fail2ban.active.map((entry) => (
                <article key={entry.ip} className="security-ops-banlist__item">
                  <strong>{entry.ip}</strong>
                  <span>{entry.country}</span>
                </article>
              )) : (
                <div className="security-ops-empty-panel">
                  <strong>Keine aktive Sperre</strong>
                  <p>Aktuell gibt es keine aktiven Sperren.</p>
                </div>
              )}
            </div>
          </section>

          <section className="dashboard-rail-card security-ops-rail security-ops-rail-compact">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Quellen</span>
                <h2 className="dashboard-section-title">Woraus wir lesen</h2>
              </div>
            </div>
            <div className="security-ops-sources">
              <article className="security-ops-source">
                <span>Debug Log</span>
                <strong>{overview.sources.debugLogPath ? "Aktiv" : "Fehlt"}</strong>
                <p>{overview.sources.debugLogPath || "-"}</p>
              </article>
              <article className="security-ops-source">
                <span>Fail2Ban Log</span>
                <strong>{overview.sources.fail2banLogPath ? "Aktiv" : "Fehlt"}</strong>
                <p>{overview.sources.fail2banLogPath || "-"}</p>
              </article>
              <article className="security-ops-source">
                <span>Security Events</span>
                <strong>{overview.summary.securityEvents}</strong>
                <p>Persistierte Signale in der Datenbank</p>
              </article>
            </div>
          </section>
        </div>

        <div className="security-ops-grid-middle">
          <article className="dashboard-surface-card security-ops-panel security-ops-panel-chart">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Vergangenheit</span>
                <h3 className="dashboard-section-title">Angriffsverlauf der letzten Tage</h3>
              </div>
              <span className="dashboard-pill">
                Letzte Sperre {formatTimestamp(overview.history.lastBanAt)}
              </span>
            </div>

            <div className="security-ops-metrics security-ops-metrics-history">
              <article className={`security-ops-metric ${metricTone(overview.summary.suspicious24h)}`}>
                <span>Auffaellig 24h</span>
                <strong>{overview.summary.suspicious24h}</strong>
                <p>Auffaellige Requests im Tagesfenster</p>
              </article>
              <article className={`security-ops-metric ${metricTone(overview.summary.blocked24h, "blocked")}`}>
                <span>Geblockt 24h</span>
                <strong>{overview.summary.blocked24h}</strong>
                <p>Geblockt durch Regel oder Status</p>
              </article>
              <article className={`security-ops-metric ${metricTone(overview.history.bans7d)}`}>
                <span>Bans 7 Tage</span>
                <strong>{overview.history.bans7d}</strong>
                <p>Neue Sperren laut Fail2Ban</p>
              </article>
              <article className={`security-ops-metric ${metricTone(overview.history.securityEvents7d)}`}>
                <span>Events 7 Tage</span>
                <strong>{overview.history.securityEvents7d}</strong>
                <p>Persistierte Security-Signale</p>
              </article>
            </div>

            <div className="security-ops-chart">
              {overview.timeline.labels.length ? (
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: "top", labels: { color: "#46556f" } } },
                    scales: {
                      x: { ticks: { color: "#6e7e98" }, grid: { color: "rgba(198,212,234,0.18)" } },
                      y: { ticks: { color: "#6e7e98", precision: 0 }, grid: { color: "rgba(198,212,234,0.16)" } },
                    },
                  }}
                />
              ) : (
                <p className="security-empty">Noch keine Verlaufsdaten fuer die letzten 7 Tage.</p>
              )}
            </div>
          </article>

          <article className={`dashboard-surface-card security-ops-panel security-ops-feed-panel${currentFeedRows.length ? "" : " is-empty"}`}>
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Live Feed</span>
                <h3 className="dashboard-section-title">Requests jetzt</h3>
              </div>
              <span className="dashboard-pill">Letzte 12 Eintraege</span>
            </div>
            <div className="security-ops-table">
              <div className="security-ops-table__head">
                <span>Zeit</span>
                <span>IP</span>
                <span>Pfad</span>
                <span>Typ</span>
                <span>Status</span>
              </div>
              {currentFeedRows.length ? currentFeedRows.map((row) => (
                <div key={row.id} className={`security-ops-table__row security-ops-table__row--${row.tone}`}>
                  <span>{row.time}</span>
                  <span>{row.ip}</span>
                  <span>{row.path}</span>
                  <span>{row.type}</span>
                  <span>{row.status}</span>
                </div>
              )) : (
                <div className="security-ops-empty-panel">
                  <strong>Keine Auffaelligkeit im Feed</strong>
                  <p>Im Moment sind keine auffaelligen Requests im Feed sichtbar.</p>
                </div>
              )}
            </div>
          </article>
        </div>

        <div className="security-ops-grid-bottom">
          <article className="dashboard-surface-card security-ops-panel">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Top Ziele</span>
                <h3 className="dashboard-section-title">Welche Pfade werden gesucht?</h3>
              </div>
            </div>
            <div className="security-ops-list">
              {overview.topTargets.length ? overview.topTargets.map((entry) => (
                <div key={entry.path} className="security-ops-list__row">
                  <div>
                    <strong>{entry.path}</strong>
                    <span>{entry.tag}</span>
                  </div>
                  <strong>{entry.count}</strong>
                </div>
              )) : <p className="security-empty">Bisher keine auffaelligen Zielpfade.</p>}
            </div>
          </article>

          <article className="dashboard-surface-card security-ops-panel">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Top IPs</span>
                <h3 className="dashboard-section-title">Wer drueckt gerade und zuletzt?</h3>
              </div>
            </div>
            <div className="security-ops-list">
              {overview.topIps.length ? overview.topIps.map((entry) => (
                <div key={entry.ip} className="security-ops-list__row">
                  <div>
                    <strong>{entry.ip}</strong>
                    <span>{entry.country} · {entry.tags.join(", ")}</span>
                  </div>
                  <div className="security-ops-list__meta">
                    <strong>{entry.count}</strong>
                    <span>{formatTimestamp(entry.lastSeen)}</span>
                  </div>
                </div>
              )) : <p className="security-empty">Noch keine aktiven Angriffsquellen erfasst.</p>}
            </div>
          </article>

          <article className={`dashboard-surface-card security-ops-panel${historyRows.length ? "" : " is-empty"}`}>
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Historie</span>
                <h3 className="dashboard-section-title">Events und Bans</h3>
              </div>
            </div>
            <div className="security-ops-table">
              <div className="security-ops-table__head security-ops-table__head--history">
                <span>Zeit</span>
                <span>Quelle</span>
                <span>Typ</span>
                <span>IP</span>
                <span>Ziel / Detail</span>
                <span>Status</span>
              </div>
              {historyRows.length ? historyRows.map((row) => (
                <div key={row.id} className={`security-ops-table__row security-ops-table__row--${row.tone} security-ops-table__row--history`}>
                  <span>{row.time}</span>
                  <span>{row.source}</span>
                  <span>{row.type}</span>
                  <span>{row.ip}</span>
                  <span>{row.target}</span>
                  <span>{row.status}</span>
                </div>
              )) : (
                <div className="security-ops-empty-panel">
                  <strong>Keine Historie sichtbar</strong>
                  <p>Noch keine historischen Security-Eintraege gefunden.</p>
                </div>
              )}
            </div>
          </article>

          <article className="dashboard-surface-card security-ops-panel">
            <div className="dashboard-section-head dashboard-section-head-compact">
              <div>
                <span className="dashboard-kicker">Muster</span>
                <h3 className="dashboard-section-title">Welche Signale dominieren?</h3>
              </div>
            </div>
            <div className="security-ops-tags">
              {overview.topTags.length ? overview.topTags.map((entry) => (
                <div key={entry.tag} className="security-ops-tag">
                  <span>{entry.label}</span>
                  <strong>{entry.count}</strong>
                </div>
              )) : <p className="security-empty">Noch keine dominanten Muster.</p>}
            </div>
          </article>
        </div>
      </section>
    </CockpitShell>
  );
}
